/**
 * Fluff Powersports Theme JavaScript
 * Shopify Online Store 2.0
 */

(function() {
  'use strict';

  // ============================================
  // Cart Functionality
  // ============================================

  class CartDrawer {
    constructor() {
      this.drawer = document.getElementById('cart-drawer');
      this.overlay = document.getElementById('cart-overlay');
      this.itemsContainer = document.getElementById('cart-items');
      this.subtotalElement = document.getElementById('cart-subtotal');
      this.footerElement = this.drawer?.querySelector('.cart-drawer-footer');
      this.checkoutButton = this.drawer?.querySelector('.btn-primary');
      this.countElements = document.querySelectorAll('.cart-count');

      this.bindEvents();
    }

    bindEvents() {
      // Open cart drawer
      document.querySelectorAll('[data-cart-toggle]').forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close cart drawer
      document.querySelectorAll('[data-cart-close]').forEach(button => {
        button.addEventListener('click', () => this.close());
      });

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    open() {
      if (this.drawer) {
        this.drawer.classList.add('is-open');
        document.body.classList.add('cart-drawer-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.fetchCart();
      }
    }

    close() {
      if (this.drawer) {
        this.drawer.classList.remove('is-open');
        document.body.classList.remove('cart-drawer-open');
        this.drawer.setAttribute('aria-hidden', 'true');
      }
    }

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    }

    async fetchCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.renderCart(cart);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }

    renderCart(cart) {
      this.updateCount(cart.item_count);
      this.updateTotals(cart);
      this.updateFooter(cart.item_count > 0);

      if (!this.itemsContainer) return;

      if (cart.items.length === 0) {
        this.itemsContainer.innerHTML = '<p class="cart-empty">Your cart is empty</p>';
        return;
      }

      this.itemsContainer.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image">
            <img src="${item.image || ''}" alt="${item.title}" loading="lazy">
          </div>
          <div class="cart-item__details">
            <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
            ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
            <div class="cart-item__quantity">
              <button type="button" class="quantity-btn" data-action="decrease" data-key="${item.key}">-</button>
              <input type="number" value="${item.quantity}" min="0" data-key="${item.key}" class="quantity-input">
              <button type="button" class="quantity-btn" data-action="increase" data-key="${item.key}">+</button>
            </div>
          </div>
          <button type="button" class="cart-item__remove" data-action="remove" data-key="${item.key}" aria-label="Remove item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `).join('');

      this.bindCartItemEvents();
    }

    bindCartItemEvents() {
      // Quantity buttons
      this.itemsContainer.querySelectorAll('[data-action]').forEach(button => {
        button.addEventListener('click', async (e) => {
          const key = button.dataset.key;
          const action = button.dataset.action;

          if (action === 'remove') {
            await this.updateQuantity(key, 0);
          } else {
            const input = this.itemsContainer.querySelector(`input[data-key="${key}"]`);
            let quantity = parseInt(input.value);

            if (action === 'increase') {
              quantity++;
            } else if (action === 'decrease' && quantity > 0) {
              quantity--;
            }

            await this.updateQuantity(key, quantity);
          }
        });
      });

      // Quantity inputs
      this.itemsContainer.querySelectorAll('.quantity-input').forEach(input => {
        input.addEventListener('change', async (e) => {
          const key = input.dataset.key;
          const quantity = parseInt(input.value) || 0;
          await this.updateQuantity(key, quantity);
        });
      });
    }

    async updateQuantity(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        const data = await response.json();

        // Check if response is an error (has status property)
        if (data.status && data.status >= 400) {
          // Show error message if available
          if (data.message || data.description) {
            this.showNotification(data.message || data.description, 'error');
          }
          // Fetch current cart state instead
          await this.fetchCart();
          return;
        }

        this.renderCart(data);
        this.dispatchCartUpdate(data);
      } catch (error) {
        console.error('Error updating cart:', error);
        // Fetch current cart state on error
        await this.fetchCart();
      }
    }

    showNotification(message, type = 'info') {
      // Create notification element if it doesn't exist
      let notification = document.getElementById('cart-notification');
      if (!notification) {
        notification = document.createElement('div');
        notification.id = 'cart-notification';
        notification.style.cssText = `
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 24px;
          border-radius: 4px;
          font-family: 'Khand', sans-serif;
          font-size: 14px;
          z-index: 10000;
          transition: opacity 0.3s ease;
        `;
        document.body.appendChild(notification);
      }

      notification.textContent = message;
      notification.style.background = type === 'error' ? '#ff0000' : '#333';
      notification.style.color = '#fff';
      notification.style.opacity = '1';

      // Hide after 3 seconds
      setTimeout(() => {
        notification.style.opacity = '0';
      }, 3000);
    }

    updateCount(count) {
      this.countElements.forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'inline-flex' : 'none';
      });
    }

    updateSubtotal(price) {
      // Legacy path — retained so external callers still work.
      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(price);
      }
    }

    updateTotals(cart) {
      // Mirrors the Liquid template's cart-drawer-totals structure: when an
      // automatic order-level discount is active we show a Subtotal /
      // Discount / Total breakdown; otherwise a single Subtotal line.
      const totalsEl = this.drawer?.querySelector('[data-cart-totals]');
      if (!totalsEl) {
        // Fallback for any older markup — just update the number.
        this.updateSubtotal(cart.total_price);
        return;
      }
      const hasDiscount = (cart.total_discount || 0) > 0;
      const t = (key, fallback) => (window.Shopify?.translations?.[key]) || fallback;
      const subtotalLabel = t('cart.general.subtotal', 'Subtotal');
      const totalLabel = t('cart.general.total', 'Total');
      let discountLabel = 'Discount';
      const apps = cart.cart_level_discount_applications || [];
      if (apps.length && apps[0].title) discountLabel = apps[0].title;
      const esc = (s) => String(s).replace(/[&<>"']/g, c =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

      if (hasDiscount) {
        totalsEl.setAttribute('data-has-discount', '');
        totalsEl.innerHTML = `
          <div class="cart-drawer-line">
            <span>${esc(subtotalLabel)}</span>
            <span data-cart-original-total>${this.formatMoney(cart.original_total_price)}</span>
          </div>
          <div class="cart-drawer-line cart-drawer-line--discount">
            <span>${esc(discountLabel)}</span>
            <span data-cart-discount>−${this.formatMoney(cart.total_discount)}</span>
          </div>
          <div class="cart-drawer-line cart-drawer-line--total">
            <span>${esc(totalLabel)}</span>
            <span id="cart-subtotal" data-cart-total>${this.formatMoney(cart.total_price)}</span>
          </div>
        `;
      } else {
        totalsEl.removeAttribute('data-has-discount');
        totalsEl.innerHTML = `
          <div class="cart-drawer-line cart-drawer-line--total">
            <span>${esc(subtotalLabel)}</span>
            <span id="cart-subtotal" data-cart-total>${this.formatMoney(cart.total_price)}</span>
          </div>
        `;
      }
      // Re-cache subtotalElement in case any legacy caller still uses it
      this.subtotalElement = document.getElementById('cart-subtotal');
    }

    updateFooter(hasItems) {
      if (this.footerElement) {
        this.footerElement.style.display = hasItems ? '' : 'none';
      }
      // Ensure checkout button has correct href
      if (this.checkoutButton && !this.checkoutButton.getAttribute('href')) {
        this.checkoutButton.setAttribute('href', '/checkout');
      }
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });
    }

    dispatchCartUpdate(cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    }
  }

  // ============================================
  // My Garage Functionality
  // ============================================

  class MyGarage {
    constructor() {
      this.storageKey = 'fluff_my_garage';
      this.modal = document.getElementById('my-garage-modal');
      this.listContainer = document.getElementById('my-garage-list');
      this.countBadge = document.querySelector('.my-garage-count');
      this.toggleButton = document.getElementById('my-garage-toggle');

      this.init();
      this.bindEvents();
      this.render();
      this.updateCount();
    }

    init() {
      // Initialize storage if it doesn't exist
      if (!localStorage.getItem(this.storageKey)) {
        localStorage.setItem(this.storageKey, JSON.stringify({ vehicles: [] }));
      }
    }

    bindEvents() {
      // Open modal
      document.querySelectorAll('[data-my-garage-toggle]').forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      // Close modal
      document.querySelectorAll('[data-my-garage-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    getData() {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : { vehicles: [] };
    }

    saveData(data) {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    }

    addVehicle(year, make, model, submodel = '') {
      const data = this.getData();

      // Check if this combination already exists
      const exists = data.vehicles.find(v =>
        v.year === year && v.make === make && v.model === model
      );

      if (exists) {
        // Update timestamp (move to top)
        exists.addedAt = Date.now();
        // Sort by newest first
        data.vehicles.sort((a, b) => b.addedAt - a.addedAt);
      } else {
        // Add new vehicle
        const vehicle = {
          id: Date.now().toString(),
          year,
          make,
          model,
          submodel,
          addedAt: Date.now(),
          label: `${year} ${make} ${model}${submodel ? ' ' + submodel : ''}`
        };

        // Add to beginning of array
        data.vehicles.unshift(vehicle);
      }

      this.saveData(data);
      this.render();
      this.updateCount();
    }

    removeVehicle(id) {
      const data = this.getData();
      data.vehicles = data.vehicles.filter(v => v.id !== id);
      this.saveData(data);
      this.render();
      this.updateCount();
    }

    render() {
      const data = this.getData();

      if (!this.listContainer) return;

      if (data.vehicles.length === 0) {
        this.listContainer.innerHTML = '<p class="my-garage-empty">No vehicles saved yet. Search for a vehicle to add it to your garage.</p>';
        return;
      }

      this.listContainer.innerHTML = data.vehicles.map(vehicle => `
        <div class="my-garage-item" data-vehicle-id="${vehicle.id}">
          <a href="/pages/part-search?year=${encodeURIComponent(vehicle.year)}&make=${encodeURIComponent(vehicle.make)}&model=${encodeURIComponent(vehicle.model)}${vehicle.submodel ? '&submodel=' + encodeURIComponent(vehicle.submodel) : ''}"
             class="my-garage-vehicle-link">
            <div class="my-garage-vehicle-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="7" width="20" height="10" rx="2"/>
                <circle cx="7" cy="17" r="2"/>
                <circle cx="17" cy="17" r="2"/>
                <path d="M2 10h20"/>
              </svg>
            </div>
            <div class="my-garage-vehicle-info">
              <strong>${vehicle.label}</strong>
              <small>Added ${this.formatDate(vehicle.addedAt)}</small>
            </div>
          </a>
          <button type="button"
                  class="my-garage-delete"
                  data-delete-vehicle="${vehicle.id}"
                  aria-label="Remove ${vehicle.label}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `).join('');

      // Bind delete buttons
      this.listContainer.querySelectorAll('[data-delete-vehicle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.dataset.deleteVehicle;
          if (confirm('Remove this vehicle from your garage?')) {
            this.removeVehicle(id);
          }
        });
      });
    }

    updateCount() {
      const data = this.getData();
      const count = data.vehicles.length;

      if (this.countBadge) {
        this.countBadge.textContent = count;
        this.countBadge.style.display = count > 0 ? 'inline-flex' : 'none';
      }
    }

    formatDate(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    open() {
      if (this.modal) {
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('my-garage-open');
        this.render(); // Refresh on open
      }
    }

    close() {
      if (this.modal) {
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('my-garage-open');
      }
    }

    isOpen() {
      return this.modal && this.modal.classList.contains('is-open');
    }

    // Public method to be called from vehicle search forms
    captureSearch(year, make, model, submodel = '') {
      if (year && make && model) {
        this.addVehicle(year, make, model, submodel);
      }
    }
  }

  // ============================================
  // Add to Cart
  // ============================================

  class AddToCart {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action="/cart/add"]');
        if (!form) return;
        if (e.submitter && e.submitter.name === 'add') {
          e.preventDefault();
          await this.handleSubmit(form, e.submitter);
        }
      });
    }

    async handleSubmit(form, button) {
      const formData = new FormData(form);
      const originalText = button.textContent;

      button.disabled = true;
      button.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const item = await response.json();

        button.textContent = 'Added!';

        // Open cart drawer if available
        if (window.cartDrawer) {
          window.cartDrawer.open();
        }

        // Dispatch event
        document.dispatchEvent(new CustomEvent('cart:added', { detail: item }));

        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);

      } catch (error) {
        console.error('Error adding to cart:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      }
    }
  }

  // ============================================
  // Variant Selector
  // ============================================

  class VariantSelector {
    constructor(container) {
      this.container = container;
      this.productData = JSON.parse(container.dataset.productJson || '{}');
      this.variantIdInput = container.querySelector('input[name="id"]');
      this.priceElement = container.querySelector('[data-product-price]');
      this.comparePriceElement = container.querySelector('[data-compare-price]');
      this.addButton = container.querySelector('[name="add"]');

      this.bindEvents();
    }

    bindEvents() {
      this.container.querySelectorAll('select[data-option-index], input[type="radio"][data-option-index]').forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.updateVariant(variant);
      } else {
        this.handleUnavailable();
      }
    }

    getSelectedOptions() {
      const options = [];

      // Handle select elements
      this.container.querySelectorAll('select[data-option-index]').forEach(select => {
        const index = parseInt(select.dataset.optionIndex);
        options[index] = select.value;
      });

      // Handle radio buttons
      this.container.querySelectorAll('input[type="radio"][data-option-index]:checked').forEach(radio => {
        const index = parseInt(radio.dataset.optionIndex);
        options[index] = radio.value;
      });

      return options;
    }

    findVariant(selectedOptions) {
      return this.productData.variants?.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    }

    updateVariant(variant) {
      // Update hidden input
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update price
      if (this.priceElement) {
        this.priceElement.textContent = this.formatMoney(variant.price);
      }

      // Update compare price
      if (this.comparePriceElement) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceElement.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceElement.style.display = '';
        } else {
          this.comparePriceElement.style.display = 'none';
        }
      }

      // Update add button
      if (this.addButton) {
        if (variant.available) {
          this.addButton.disabled = false;
          this.addButton.textContent = this.addButton.dataset.addText || 'Add to cart';
        } else {
          this.addButton.disabled = true;
          this.addButton.textContent = this.addButton.dataset.soldOutText || 'Sold out';
        }
      }

      // Dispatch event
      document.dispatchEvent(new CustomEvent('variant:changed', { detail: variant }));
    }

    handleUnavailable() {
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Unavailable';
      }
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      });
    }
  }

  // ============================================
  // Collapsible Elements
  // ============================================

  class Collapsible {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('[data-collapsible-trigger]').forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(trigger);
        });

        // Keyboard accessibility
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(trigger);
          }
        });
      });
    }

    toggle(trigger) {
      const target = document.getElementById(trigger.getAttribute('aria-controls'));
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      trigger.setAttribute('aria-expanded', !isExpanded);

      if (target) {
        target.hidden = isExpanded;
        target.classList.toggle('is-open', !isExpanded);
      }
    }
  }

  // ============================================
  // Mobile Menu
  // ============================================

  class MobileMenu {
    constructor() {
      this.toggle = document.querySelector('[data-mobile-menu-toggle]');
      this.menu = document.querySelector('[data-mobile-menu]');

      if (this.toggle && this.menu) {
        this.bindEvents();
      }
    }

    bindEvents() {
      this.toggle.addEventListener('click', () => this.toggleMenu());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    toggleMenu() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.menu.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mobile-menu-open');
    }

    close() {
      this.menu.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mobile-menu-open');
    }

    isOpen() {
      return this.menu.classList.contains('is-open');
    }
  }

  // ============================================
  // Quantity Selector
  // ============================================

  class QuantitySelector {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('.quantity-selector').forEach(selector => {
        const input = selector.querySelector('input[type="number"]');
        const decreaseBtn = selector.querySelector('[data-quantity-decrease]');
        const increaseBtn = selector.querySelector('[data-quantity-increase]');

        if (!input) return;

        if (decreaseBtn) {
          decreaseBtn.addEventListener('click', () => {
            const min = parseInt(input.min) || 1;
            if (input.value > min) {
              input.value = parseInt(input.value) - 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        if (increaseBtn) {
          increaseBtn.addEventListener('click', () => {
            const max = parseInt(input.max) || Infinity;
            if (input.value < max) {
              input.value = parseInt(input.value) + 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });
    }
  }

  // ============================================
  // Product Image Gallery
  // ============================================

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('[data-main-image]');
      this.thumbnails = container.querySelectorAll('[data-thumbnail]');

      if (this.mainImage && this.thumbnails.length) {
        this.bindEvents();
      }
    }

    bindEvents() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectImage(thumb);
        });

        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectImage(thumb);
          }
        });
      });

      // Listen for variant changes
      document.addEventListener('variant:changed', (e) => {
        const variant = e.detail;
        if (variant.featured_image) {
          this.selectImageBySrc(variant.featured_image.src);
        }
      });
    }

    selectImage(thumb) {
      const src = thumb.dataset.fullSrc || thumb.querySelector('img')?.src;
      const alt = thumb.dataset.alt || thumb.querySelector('img')?.alt;

      if (src && this.mainImage) {
        this.mainImage.src = src;
        this.mainImage.alt = alt || '';

        this.thumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }
    }

    selectImageBySrc(src) {
      const thumb = Array.from(this.thumbnails).find(t => {
        const thumbSrc = t.dataset.fullSrc || t.querySelector('img')?.src;
        return thumbSrc && thumbSrc.includes(src.split('?')[0]);
      });

      if (thumb) {
        this.selectImage(thumb);
      }
    }
  }

  // ============================================
  // Initialize
  // ============================================

  function init() {
    // Global cart drawer
    window.cartDrawer = new CartDrawer();

    // Global My Garage
    window.myGarage = new MyGarage();

    // Add to cart handling
    new AddToCart();

    // Collapsible elements
    new Collapsible();

    // Mobile menu
    new MobileMenu();

    // Quantity selectors
    new QuantitySelector();

    // Product variant selectors
    document.querySelectorAll('[data-product-form]').forEach(container => {
      new VariantSelector(container);
    });

    // Product galleries
    document.querySelectorAll('[data-product-gallery]').forEach(container => {
      new ProductGallery(container);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
